/**
 * The concierge agent — everything about it that is a pure function.
 *
 * WHY THIS FILE EXISTS SEPARATELY
 * ------------------------------
 * Same reason as `convex/lib/checkoutGate.ts`: the decisions worth testing
 * (is the agent on, what may this visitor still say, what does the model get
 * told) must be reachable from Jest, and anything importing `_generated`
 * is not. So the rules live here as plain functions over plain data, and
 * `convex/conciergeAgent.ts` is the thin layer that talks to the database and
 * the Anthropic API.
 *
 * WHAT THE AGENT IS FOR, IN ONE LINE
 * ----------------------------------
 * It ONBOARDS a collector — it welcomes them, gets to know them, and writes
 * what it learns to `collectorProfiles.facts`, the same store the quiz and the
 * Combination write to, so no surface ever re-asks a question already answered.
 *
 * Support is the DUTY, not the job. If they arrive with a problem it is
 * answered first and honestly, because an agent that interviews someone who
 * asked where their parcel is has failed at both. But when nothing is on fire,
 * the reason this widget exists is to learn who it is talking to.
 *
 * WHAT IT MUST NEVER DO
 * ---------------------
 * The scripted concierge it replaces carried two hard-won promises and this
 * inherits both: it never claims to see order status (Amazon owns fulfilment,
 * we cannot), and it never promises an email the deployment cannot send. Both
 * are places where the comfortable answer is a lie the customer finds out
 * about later. They are restated in the system prompt because the model, not
 * a decision tree, is the thing that has to keep them now.
 */

import {
  INTERVIEW_KEYS,
  INTERVIEW_MODE,
  interviewSection,
  type InterviewState,
} from './collectorInterview';

// ========================================
// MODEL
// ========================================

/**
 * The model. See `docs/` note in `AGENTS.md` before changing it.
 *
 * Opus 5 at LOW effort rather than a smaller model. The brief asked for quick
 * responses, and effort is the latency lever that does not cost capability the
 * way dropping a tier does — low effort on the current model routinely beats
 * high effort on the previous one. Thinking is deliberately left ON (adaptive,
 * the default): disabling it on Opus 5 makes the model occasionally write a
 * tool call into its visible text instead of emitting a `tool_use` block,
 * which in a loop like this one fails silently and pollutes later turns.
 */
export const CONCIERGE_MODEL = 'claude-opus-5';

/** Latency lever. `low` is the documented setting for chat-shaped routes. */
export const CONCIERGE_EFFORT = 'low' as const;

/**
 * A concierge reply is a couple of sentences — call it eighty tokens. This is a
 * ceiling that stops a runaway, not a target, so it keeps roughly ten times the
 * headroom a normal reply needs. It was 2048, which bought nothing except a
 * larger worst case on every abusive request.
 */
export const CONCIERGE_MAX_TOKENS = 800;

/**
 * Tool-use rounds per turn.
 *
 * Two, which is what this comment always said was enough — "remember this, then
 * answer" — while the constant sat at 4. It is a straight cost multiplier: each
 * round is another API call carrying the WHOLE history, so the ceiling was
 * double what anything needed.
 */
export const MAX_TOOL_ROUNDS = 2;

// ========================================
// THE GATE
// ========================================

/** Env var name, exported so error messages can name the thing to flip. */
export const CONCIERGE_AGENT_FLAG = 'CONCIERGE_AGENT_ENABLED';

/**
 * Whether the agent may run at all. TWO deliberate acts are required.
 *
 * FAILS CLOSED on both, and the shape is copied from `checkoutGate.ts` on
 * purpose: the deployment most likely to be missing configuration is a new or
 * restored one, and that is exactly the deployment that must not start spending
 * money on its own initiative. An absent key is not an error state — the
 * scripted concierge still runs — it is simply "the agent is not available
 * here".
 *
 * WHY A FLAG AS WELL AS THE KEY. Deleting `ANTHROPIC_API_KEY` was the only
 * off-switch, and it is the wrong one: the key may be wanted for other things,
 * and re-setting a secret under pressure is slower and riskier than flipping a
 * boolean. This is the control you reach for when the concierge is costing
 * money you did not intend to spend:
 *
 *     npx convex env set CONCIERGE_AGENT_ENABLED false [--prod]
 *
 * Anything but the exact string "true" — unset, empty, "1", "TRUE", a stray
 * space — reads as OFF.
 */
export function isAgentEnabled(
  apiKey: string | undefined | null,
  env: Record<string, string | undefined> = process.env,
): boolean {
  const hasKey = typeof apiKey === 'string' && apiKey.trim().length > 0;
  return hasKey && env[CONCIERGE_AGENT_FLAG] === 'true';
}

// ========================================
// LIMITS
// ========================================

/**
 * How much conversation a visitor gets.
 *
 * Chat deliberately does NOT require an account — the brief is explicit, and a
 * sign-in wall in front of "can this binder hold my collection" would lose the
 * exact person the widget exists to help. So the account earns a HIGHER
 * ceiling rather than being the price of entry.
 *
 * These are per rolling window and enforced server-side in
 * `conciergeAgent.send`; the client shows the remaining count so the limit is
 * never a surprise mid-sentence.
 */
export const CHAT_LIMITS = {
  anonymous: { maxMessages: 12, windowMs: 60 * 60 * 1000, action: 'concierge_chat_anon' },
  authenticated: { maxMessages: 40, windowMs: 60 * 60 * 1000, action: 'concierge_chat_user' },
} as const;

export function limitsFor(isAuthenticated: boolean): (typeof CHAT_LIMITS)[keyof typeof CHAT_LIMITS] {
  return isAuthenticated ? CHAT_LIMITS.authenticated : CHAT_LIMITS.anonymous;
}

/**
 * The GLOBAL ceiling — the only limit that actually bounds what a bad day costs.
 *
 * WHY THE PER-VISITOR LIMIT IS NOT ENOUGH, AND NEVER WAS
 * `CHAT_LIMITS` keys on `user?._id ?? sessionId`, and `sessionId` is a string
 * the CLIENT supplies. It comes from localStorage in a real browser, but the
 * mutation accepts anything — so a script that mints a fresh id per request
 * gets a fresh allowance every time, and the per-visitor ceiling stops only
 * honest people. That is not a bug in `checkRateLimit`; it is what happens when
 * a limit is bound to an identity the caller controls.
 *
 * These caps are bound to nothing at all, which is exactly why they hold. They
 * are the ceiling on TOTAL spend regardless of who is calling or how many
 * identities they invent, and they are the first thing to lower if the bill
 * looks wrong.
 *
 * Deliberately two windows: the hourly cap absorbs a burst without killing a
 * busy afternoon, the daily cap bounds a sustained attack that paces itself
 * under the hourly one.
 */
export const GLOBAL_LIMITS = {
  hour: { maxMessages: 400, windowMs: 60 * 60 * 1000, action: 'concierge_global_hour' },
  day: { maxMessages: 2000, windowMs: 24 * 60 * 60 * 1000, action: 'concierge_global_day' },
} as const;

/** Identifier for the global counters. Constant on purpose — it is everyone. */
export const GLOBAL_IDENTIFIER = 'GLOBAL';

/**
 * Warn before the ceiling, not just at it.
 *
 * A cap you find out about the next morning is the same as no cap: by then the
 * spend has happened and the concierge has been refusing customers for hours.
 */
export const GLOBAL_WARN_FRACTION = 0.8;

export function shouldWarnAboutBudget(used: number, max: number): boolean {
  if (max <= 0) {
    return false;
  }
  return used / max >= GLOBAL_WARN_FRACTION;
}

/** What a customer is told when the global ceiling is reached. */
export const BUDGET_EXHAUSTED_MESSAGE =
  'I’m taking a short break — I’ve been talking all day. Try me again a little later, or the contact page reaches a person now.';

/**
 * Longest single message accepted.
 *
 * Lowered from 4000: it is the attacker's cheapest lever on input cost, and
 * 1500 characters is still several paragraphs — far more than anyone types into
 * a chat box. `tickets.ts` keeps its own 4000 cap for the contact form, which
 * is a different surface with a different job.
 */
export const MAX_MESSAGE_LENGTH = 1500;

/**
 * Turns of history replayed to the model.
 *
 * The API is stateless, so every turn resends the conversation. A cap keeps
 * both latency and cost flat on a long chat; the profile carries anything that
 * actually mattered, because the agent was told to write it down.
 */
export const MAX_HISTORY_MESSAGES = 16;

// ========================================
// THE FACT VOCABULARY
// ========================================

/**
 * Keys the agent may write. A CLOSED set, and that is the point.
 *
 * `collectorProfiles` is shared with the quiz (`src/lib/combination.ts`) and
 * the scripted concierge (`src/content/concierge.ts`), and its whole value is
 * that a key means the same thing everywhere. A model inventing `collection_sz`
 * alongside `collectionSize` would quietly split the profile in two and the
 * "never re-ask a question" property would rot without anything failing.
 *
 * The first block is the EXISTING vocabulary, copied from those two files —
 * never rename these. The second block is what the discovery ladder adds.
 */
export const FACT_KEYS = {
  // — already written by the quiz / Combination / scripted concierge —
  primaryUse: 'What they use a binder for',
  collectionSize: 'Roughly how many cards they hold',
  priority: 'What matters most to them in storage',
  motivation: 'Why they collect',
  firstCard: 'The card that started it',
  intent: 'What they came here to do',
  storageToday: 'What they store cards in right now',
  yearsCollecting: 'How long they have collected',
  biggestLoss: 'A card or collection they lost or damaged',
  nextPurchase: 'What they plan to buy next',
  discovery: 'How they found us',
  gradingIntent: 'Whether they grade or plan to',

  // — the discovery ladder, new here —
  collectingJoy: 'A good memory of collecting — the part they love',
  painPoint: 'The concrete problem their storage causes them today',
  frustration: 'What annoys them about the products they have tried',
  nightmareScenario: 'The outcome they are quietly afraid of',
  dreamScenario: 'What "sorted" would actually look like for them',

  // — the collector conversation (interview mode), Matthew's six questions —
  collectingStage: 'Where they are right now in their collecting journey',
  idealBrand: 'What the ideal TCG brand would do for them',
  problemsSolved: 'The problems that brand would solve for them',
  challenges: 'The challenges it would help them overcome',
  belonging: 'The belonging and community they would want from it',
  payMoreFor: 'What they would gladly pay more for if we reached for the stars',
} as const;

export type FactKey = keyof typeof FACT_KEYS;

/** Reject anything outside the vocabulary before it reaches the profile. */
export function isKnownFactKey(key: string): key is FactKey {
  return Object.prototype.hasOwnProperty.call(FACT_KEYS, key);
}

// ========================================
// WHAT "ONBOARDED" MEANS
// ========================================

/**
 * The facts that constitute a complete-enough profile, per rung, in the order
 * worth asking them.
 *
 * This is a SUBSET of `FACT_KEYS`, not all of it — the rest (`firstCard`,
 * `discovery`, `gradingIntent` …) are welcome if they come up, but chasing all
 * eighteen would be an interrogation. Onboarding has to be finishable, and
 * `depthFor` cannot report `complete` without a definition of done.
 */
export const ONBOARDING_TARGETS = {
  /** The everyday set. Anyone will answer these. */
  profiling: ['primaryUse', 'collectionSize', 'yearsCollecting', 'storageToday'],
  /** The discovery-call set. Only asked once the ladder says they are earned. */
  discovery: ['collectingJoy', 'painPoint', 'nightmareScenario', 'dreamScenario'],
} as const satisfies Record<string, readonly FactKey[]>;

/** Every target across both rungs — the definition of a finished onboarding. */
export const ALL_TARGETS: readonly FactKey[] = [
  ...ONBOARDING_TARGETS.profiling,
  ...ONBOARDING_TARGETS.discovery,
];

/**
 * The gaps worth closing next, given what is already known.
 *
 * WHY THIS EXISTS AT ALL
 * The prompt already lists what we know and forbids re-asking it. That tells
 * the model what NOT to say and leaves it to pick the next question by feel —
 * which is how an onboarding chat wanders. Naming the gaps, in order, is the
 * difference between an agent that is curious and one that is actually
 * onboarding somebody.
 *
 * Profiling gaps are always listed first, even at discovery depth: someone can
 * reach discovery depth by writing at length without ever saying how many cards
 * they own, and the everyday facts are the ones the rest of the site uses.
 */
export function missingFor(depth: Depth, knownKeys: readonly string[]): FactKey[] {
  const known = new Set(knownKeys);
  const wanted: readonly FactKey[] =
    depth === 'discovery' || depth === 'complete'
      ? ALL_TARGETS
      : ONBOARDING_TARGETS.profiling;
  return wanted.filter((key) => !known.has(key));
}

// ========================================
// THE DISCOVERY LADDER
// ========================================

/**
 * How deep to go, decided from how much the visitor is actually giving us.
 *
 * The brief: ask a few profiling questions like the quiz does, and IF they are
 * offering a lot, escalate into discovery-call territory — the good times, the
 * pain, the nightmare, the dream. The failure mode to design against is asking
 * a stranger who typed "do you ship to canada" what they are afraid of.
 *
 * So depth is EARNED, and the signal is the visitor's own investment: how much
 * they have written, over how many turns. Deliberately a floor on both — one
 * long paragraph is a question, not a conversation.
 */
export type Depth = 'greeting' | 'profiling' | 'discovery' | 'complete';

export function depthFor(input: {
  visitorTurns: number;
  visitorCharacters: number;
  knownKeys: readonly string[];
}): Depth {
  const { visitorTurns, visitorCharacters, knownKeys } = input;
  const factsKnown = knownKeys.length;

  // Onboarding is FINISHED. Checked before the greeting case on purpose: a
  // collector we already know everything about must not be re-interviewed just
  // because this is a fresh conversation.
  if (missingFor('discovery', knownKeys).length === 0) {
    return 'complete';
  }
  if (visitorTurns === 0) {
    return 'greeting';
  }
  // Three or more real exchanges AND real substance in them — roughly a
  // sentence and a half per turn, sustained. Someone answering "yes / no / ok"
  // is being helped, not interviewed, and stays at profiling depth forever.
  const averageLength = visitorCharacters / Math.max(1, visitorTurns);
  const investing = visitorTurns >= 3 && averageLength >= 60;
  // Someone who already told us a lot has earned it too, however tersely.
  return investing || factsKnown >= 5 ? 'discovery' : 'profiling';
}

// ========================================
// TOOLS
// ========================================

/**
 * The tool surface, kept deliberately small.
 *
 * Three tools, because each one is something the model genuinely cannot do by
 * talking: write to the profile, raise a ticket a human will answer, and point
 * at a product page. Everything the agent KNOWS is injected into the system
 * prompt instead of fetched through a tool — a round trip to read a profile we
 * already have in hand would show up as latency the brief explicitly rules out.
 *
 * `strict: true` guarantees the arguments validate against the schema, so the
 * handler never has to defend against a missing field.
 */
export interface AgentTool {
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, unknown>;
    required: string[];
    additionalProperties: false;
  };
  strict: true;
}

export const AGENT_TOOLS: AgentTool[] = [
  {
    name: 'remember',
    description:
      'Save something the collector has told you about themselves so no other part of the site ever asks them again. Call this the moment you learn something, not at the end of the conversation. Only ever record what they actually said — never a guess, never an inference you would not say out loud to them.',
    input_schema: {
      type: 'object',
      properties: {
        key: {
          type: 'string',
          enum: Object.keys(FACT_KEYS),
          description: 'Which fact this is. Must be one of the listed keys.',
        },
        value: {
          type: 'string',
          description:
            'What they told you, in their words where possible, condensed to a phrase or short sentence.',
        },
      },
      required: ['key', 'value'],
      additionalProperties: false,
    },
    strict: true,
  },
  {
    name: 'raise_ticket',
    description:
      'Hand this conversation to a human and give the collector a reference number. Use it for anything you cannot answer honestly yourself — order and delivery questions above all, since Amazon fulfils and we cannot see their status. Say you are doing it before you do it.',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description:
            'The whole question in the collector\'s own words, plus any context from this chat a human would need to answer it without asking again.',
        },
        email: {
          type: 'string',
          description:
            'Their reply address if they have given you one. Send an EMPTY STRING if they have not — a signed-in collector already has one on file, and if we still need one the tool will tell you to ask.',
        },
      },
      required: ['message', 'email'],
      additionalProperties: false,
    },
    strict: true,
  },
  {
    name: 'show_product',
    description:
      'Put a product card in the conversation when a specific product genuinely answers what they asked. One at a time, and only when it fits what they have told you — an unprompted recommendation reads as a sales pitch and ends the conversation you are trying to have.',
    input_schema: {
      type: 'object',
      properties: {
        slug: {
          type: 'string',
          description: 'The product slug, exactly as given in the catalogue section of your instructions.',
        },
        why: {
          type: 'string',
          description: 'One short line, addressed to them, on why this one and not another.',
        },
      },
      required: ['slug', 'why'],
      additionalProperties: false,
    },
    strict: true,
  },
];

/**
 * The interview mode's three extra tools. Offered ONLY in that mode: the
 * onboarding concierge has no business asking for consent, and a closed tool
 * surface per mode is what keeps each mode honest about what it can do.
 */
export const INTERVIEW_TOOLS: AgentTool[] = [
  {
    name: 'record_answer',
    description:
      'Save the collector\'s answer to one of the six conversation questions, in their own words, the moment they give it. One call per question; calling again for the same question replaces the earlier answer.',
    input_schema: {
      type: 'object',
      properties: {
        question: {
          type: 'string',
          enum: [...INTERVIEW_KEYS],
          description: 'Which of the six questions this answers.',
        },
        answer: {
          type: 'string',
          description: 'What they said, condensed only as much as needed to fit — keep their words and their examples.',
        },
      },
      required: ['question', 'answer'],
      additionalProperties: false,
    },
    strict: true,
  },
  {
    name: 'request_consent',
    description:
      'Put the email consent sentence and an "I agree" button in front of the collector for the address they gave you. Call it once, after they have told you where the reward should go. You do not decide consent — they tap the button, and the server records it. If the tool says the address is not usable, ask them to check it.',
    input_schema: {
      type: 'object',
      properties: {
        email: {
          type: 'string',
          description: 'The email address they gave, exactly as they wrote it.',
        },
      },
      required: ['email'],
      additionalProperties: false,
    },
    strict: true,
  },
  {
    name: 'complete_interview',
    description:
      'Finish the conversation and send the reward email. Only works once they have tapped "I agree" and enough questions are answered; if it refuses, it tells you what is still missing. After it succeeds, say the closing line and stop asking questions.',
    input_schema: {
      type: 'object',
      properties: {},
      required: [],
      additionalProperties: false,
    },
    strict: true,
  },
];

export type AgentMode = 'onboarding' | typeof INTERVIEW_MODE;

/** The tool surface for a thread's mode. */
export function toolsFor(mode: AgentMode | undefined): AgentTool[] {
  return mode === INTERVIEW_MODE ? [...AGENT_TOOLS, ...INTERVIEW_TOOLS] : AGENT_TOOLS;
}

// ========================================
// THE SYSTEM PROMPT
// ========================================

export interface ProfileFact {
  key: string;
  value: string;
  source: string;
}

export interface CatalogueEntry {
  slug: string;
  name: string;
  summary: string;
}

export interface PromptInput {
  facts: ProfileFact[];
  firstName?: string;
  archetype?: string;
  depth: Depth;
  page: string;
  isAuthenticated: boolean;
  catalogue: CatalogueEntry[];
  /** Absent means the ordinary onboarding concierge. */
  mode?: AgentMode;
  /** Required when `mode` is the interview. */
  interview?: InterviewState;
}

/**
 * Everything the model is told, assembled in a STABLE order.
 *
 * Order matters for more than tidiness: the prompt is cached by prefix, so the
 * unchanging half (who you are, the rules, the catalogue) is emitted first and
 * the volatile half (this visitor, this page) last. Putting the page path at
 * the top would invalidate the cache on every navigation and pay full price
 * for every message.
 */
export function buildSystemPrompt(input: PromptInput): { stable: string; volatile: string } {
  const stable = [
    STATIC_PERSONA,
    catalogueSection(input.catalogue),
  ].join('\n\n');

  const missing = missingFor(input.depth, input.facts.map((fact) => fact.key));

  // Interview mode swaps the onboarding ladder for the six-question script.
  // The profile section stays: the model must still not re-ask what it knows.
  const jobSections =
    input.mode === INTERVIEW_MODE
      ? [
          interviewSection(
            input.interview ?? { answered: input.facts.map((fact) => fact.key) },
          ),
        ]
      : [
          gapsSection(missing),
          depthSection(input.depth),
          accountInviteSection(input.isAuthenticated, input.facts.length),
        ];

  const volatile = [
    profileSection(input.facts, input.firstName, input.archetype),
    ...jobSections,
    `## Right now\n\nThey are on \`${input.page}\`. They are ${
      input.isAuthenticated ? 'signed in' : 'not signed in, and do not need to be to talk to you'
    }.`,
  ]
    .filter((section) => section.length > 0)
    .join('\n\n');

  return { stable, volatile };
}

const STATIC_PERSONA = `You are the concierge for InfinityVault, which makes premium binders and storage for trading-card collectors.

## Voice

Quiet confidence. We don't shout; the work speaks. Warm, unhurried, specific. You are a person who knows cards, not a support macro and not a salesperson.

Write SHORT. Two or three sentences is a normal reply, and one is often better. Never bullet-point a conversation. Ask ONE question at a time and then actually wait — stacking two questions is how a chat turns into a form.

Never say "I'd be happy to", "Great question", "Absolutely", or "As an AI". Never open by restating what they just said.

## What you are actually doing

**You are onboarding a collector.** That is the job. You are the first person from InfinityVault they have spoken to, and your purpose is to welcome them and genuinely get to know them — the way a good shop owner does with someone new through the door. Everything you learn is saved to their profile, so no other part of this site ever asks them the same thing twice.

**Answering their questions is a duty that outranks the job whenever it applies.** If they came with a problem, solve it first, honestly, and do not interview someone who asked where their parcel is. If they are upset or in a hurry, drop the onboarding entirely until it is sorted. Answer, THEN ask — and ask ONE thing.

But when nothing is on fire, do not sit waiting to be useful. Be curious. A reply that answers a question and asks nothing back is a missed introduction.

## Hard rules — these are promises the site has already made

- You CANNOT see order status, tracking, or delivery dates. Amazon fulfils these orders and we genuinely have no visibility. Never guess, never reassure, never say "it should arrive". Say plainly that you cannot see it, and raise a ticket.
- Never promise an email will arrive. If someone needs a reply, raise a ticket and read them the reference — that reference is the thing they can rely on.
- Never invent a product, a price, a spec, a discount or a delivery time. Only the catalogue below is real. If you don't know, say so.
- Never ask for a card number, a password, or a full address.
- If they ask something upsetting or personal, drop the profiling entirely and just be decent to them.

## Remembering

Call \`remember\` the moment you learn something real about them — not at the end. Record what they SAID, not what you concluded. If they contradict something already in their profile, believe the newer thing and save it; people change their minds, and the profile is built to show the disagreement rather than hide it.`;

function catalogueSection(catalogue: CatalogueEntry[]): string {
  if (catalogue.length === 0) {
    // An empty catalogue is a real state — a fresh deployment, or a storefront
    // with nothing visible. Saying so beats the model improvising a product.
    return '## The catalogue\n\nNothing is available to show right now. Do not name or describe any product; if they ask what we sell, say you cannot pull the range up at the moment and offer to have someone follow up.';
  }
  const lines = catalogue.map((item) => `- \`${item.slug}\` — **${item.name}**. ${item.summary}`);
  return `## The catalogue\n\nThese are the only products that exist. Use the slug exactly as written when you call \`show_product\`.\n\n${lines.join('\n')}`;
}

function profileSection(facts: ProfileFact[], firstName?: string, archetype?: string): string {
  const header = '## Who you are talking to';
  if (facts.length === 0 && !firstName && !archetype) {
    return `${header}\n\nYou have never met them. You know nothing about them yet — do not pretend otherwise, and do not greet them as if you have.`;
  }
  const lines: string[] = [];
  if (firstName) {
    lines.push(`- Their name is ${firstName}.`);
  }
  if (archetype) {
    lines.push(`- Their collector archetype came out as "${archetype}".`);
  }
  for (const fact of facts) {
    const label = isKnownFactKey(fact.key) ? FACT_KEYS[fact.key] : fact.key;
    lines.push(`- ${label}: ${fact.value} _(from the ${fact.source})_`);
  }
  return `${header}\n\nThey have already told us this. **Never ask about any of it again** — refer to it naturally instead, the way someone who remembered would.\n\n${lines.join('\n')}`;
}

/**
 * The gaps, named explicitly and in order.
 *
 * The profile section above says what we KNOW and forbids re-asking it. On its
 * own that tells the model what not to say and leaves it to choose the next
 * question by feel, which is how an onboarding conversation wanders and then
 * stops. Naming what is still missing is the difference between an agent that
 * is merely curious and one that is actually onboarding somebody.
 *
 * It is a priority list, NOT a script — the instruction to pick the one that
 * follows from what they just said matters as much as the list itself, because
 * a model handed an ordered list will otherwise work down it like a form.
 */
function gapsSection(missing: FactKey[]): string {
  const header = '## What you still do not know about them';
  if (missing.length === 0) {
    return `${header}\n\nNothing on the onboarding list is outstanding — you know everything you set out to learn. Stop asking onboarding questions.`;
  }
  const lines = missing.map((key) => `- ${FACT_KEYS[key]} (\`${key}\`)`);
  return `${header}\n\nThese are the gaps, roughly in the order worth closing them. Pick the ONE that follows most naturally from whatever they just said — never work down this list mechanically, and never ask for something they have already implied.\n\n${lines.join('\n')}`;
}

/**
 * The account, offered at the one moment it is not a sales pitch.
 *
 * Chat deliberately needs no account, so this is never a gate — but once
 * somebody has told us a few real things, "so I remember this next time" is a
 * true and useful sentence, and it happens to raise their own ceiling from 12
 * messages an hour to 40. Offering it before they have got anything is just a
 * signup prompt wearing a friendly voice, so it stays silent until then, and
 * never appears at all for someone already signed in.
 */
function accountInviteSection(isAuthenticated: boolean, factsKnown: number): string {
  if (isAuthenticated || factsKnown < 3) {
    return '';
  }
  return `## Worth mentioning once

They have told you a few things now and they are NOT signed in, so everything they have said is tied to this browser alone. Once — not repeatedly, and only where it fits — mention that making an account means you remember all of this next time, and that it lets you talk for longer. Do not pitch it, do not make it a condition of anything, and drop it entirely if they ignore it.`;
}

/**
 * The ladder. Each rung names what to ask AND what not to yet.
 *
 * The discovery rung is the one the brief cared about, and its instruction is
 * mostly about restraint: these are questions people answer honestly only when
 * they are asked lightly and one at a time.
 */
function depthSection(depth: Depth): string {
  const header = '## How far to go right now';
  if (depth === 'complete') {
    return `${header}\n\n**Onboarding is done. Stop interviewing.** You know everything you set out to learn about them, and continuing to ask is what turns a welcome into an interrogation.

From here you are simply useful: answer what they ask, and use what you know rather than collecting more. Referring back to something they told you earlier — accurately, without making a performance of it — is the whole payoff for what they gave you.

If something genuinely new comes up in conversation, still call \`remember\`. Just do not go looking.`;
  }
  if (depth === 'greeting') {
    return `${header}\n\nThey have not said anything yet, and this is the introduction. Open with one short, warm line and ONE easy question — what they collect, or what brought them in. Do not stack questions. Do not pitch. Do not list what you can help with; nobody reads a menu.`;
  }
  if (depth === 'profiling') {
    return `${header}\n\nThis is the everyday part of getting to know them, and it should feel like conversation rather than a form. Answer whatever they asked, then ask ONE thing from the gap list above — what they collect, how long, roughly how big the collection is, what they keep it in now.

Keep it light and take the answer at face value; you are building a picture, not auditing them. Do NOT go anywhere near fears, losses or dreams yet — they have not given you enough for that to land as interest rather than intrusion.`;
  }
  return `${header}\n\nThey are giving you real answers, so you have earned deeper ground. Go there gently, ONE question at a time, and only ever as a follow-up to something they actually said — never as a change of subject.

What you are trying to understand, roughly in this order:

- **The good part.** The card that started it, the pull they still remember, what collecting gives them. Ask this first; it is the easiest to answer and it sets the register for the rest.
- **The friction.** What their current storage actually costs them — the sleeve that creases, the binder that will not close, the twenty minutes finding one card.
- **What it nearly cost.** A card that got damaged, or lost, or that they stopped taking anywhere because they did not trust the carry. Ask about the past, not the fear — "has that ever gone wrong for you?" gets a true answer where "what are you afraid of?" gets a flinch.
- **What sorted looks like.** If the storage problem simply went away, what would they do that they do not do now? This is the one that tells us what we are actually selling.

Ask these the way a person would: short, curious, and specific to what they just told you. If they give a one-word answer, take the hint and step back down — do not push twice.`;
}
